import React from "react";
import { sortableRowProps } from "./sortableRows.js";
import { C } from "../lib/colors.js";
import { forecastDisplayName } from "../lib/forecast/displayName.js";
import { extractDistribution, bucketLabel, resolvedBucketIndex } from "../lib/forecast/multiway.js";
import { forecastRowBadge } from "../lib/forecastBadge.js";
import CollabBadge from "./CollabBadge.jsx";
import { hasCollabBadge } from "../lib/forecast/collabBadge.js";
import { fmtRelative } from "../lib/formatDates.js";
import { liveContractMomentOf } from "../lib/forecast/reportPointer.js";
import { TRACKING_CAD } from "../lib/tracking.js";
import { resolvedOutcome } from "../lib/forecast/resolvedOutcome.js";
import { isReadOnlyRow } from "../lib/forecast/sharedRowGuards.js";
import { isSayerManaged } from "../lib/featured/observerCopy.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { answerSummary } from "../lib/forecast/answerSummary.js";
import { listSource } from "../lib/forecast/listSource.js";
import { toast } from "../lib/ui/toast.js";
import { Chip, IconButton, Spinner } from "./ui/Primitives.jsx";
import {
  IconAlert,
  IconArrowRight,
  IconBell,
  IconCheck,
  IconEye,
  IconMore,
  IconShare,
  IconTrash,
  IconClose,
  IconReforecast,
} from "./ui/Icons.jsx";
import { sentenceCase } from "./sentenceCase.js";

// Compare-lane color + active-highlight logic shared by WatchlistRow / RecentRow.
// compareLane is the row's index in the active comparison (0 =
// primary/top lane, 1 = secondary/bottom lane), or <0 when it's not a selected
// compare root. Outline it in the SAME color the timeline uses for that lane
// (LANE_COLORS=[purple, green]) so it's obvious which entry is top vs bottom.
// In compare mode the lane color is the ONLY highlight — suppress the plain
// active-purple so a row toggled OUT of the comparison reads as clearly
// unselected, instead of falling back to a near-identical active-purple
// (border + rowActive bg, no ring) that looked like a different shade.

function laneRowStyle(compareLane, compareMode, isActive) {
  const laneColor = compareLane === 0 ? C.purple : compareLane === 1 ? C.green : null;
  const showActive = isActive && !compareMode;
  return { laneColor, showActive };
}

// Shift+click in Chrome extends the text selection across rows — suppress it.
// Shared by WatchlistRow / RecentRow's onMouseDown.
function suppressShiftSelect(e) {
  if (e.shiftKey) e.preventDefault();
}

/**
 * The bucket a resolution NAMED as having occurred, or null when it named none.
 *
 * A resolved MULTIWAY card states the winning bucket, not just a verdict word: "3% TO 4% ·
 * OCCURRED" says which outcome happened, where a bare "OCCURRED" only says that one did.
 *
 * `isBinary` exists because that reasoning inverts for a yes/no question, whose buckets ARE
 * the verdicts — naming the bucket there produces "DOES NOT OCCUR · OCCURRED", which is
 * literally true and unreadable. Binary cards show the verdict word alone instead.
 *
 * Resolved BY LABEL through `resolvedBucketIndex`, the same call the timeline marker uses, so
 * the card and the marker cannot disagree about which bucket won.
 *
 * @param {object} f forecast row
 * @param {string|null} status the resolution status in force (debug override included)
 * @returns {{label: string, isBinary: boolean}|null} null when no bucket was named
 */
// Gold marks a settled card — now on the CHECK rather than a pill fill. The palette's
// `gold` token, not `C.today`: that one (despite older comments calling it the gold
// accent) resolves to a purple in the dark theme, which would make the settled mark
// indistinguishable from the accent the in-flight states use. Dawn draws it green.
const RESOLVED_GOLD = C.gold;

function resolvedWinner(f, status) {
  const contract = f?.contract || null;
  if (!contract) return null;
  const dist = extractDistribution(contract, {
    allMultiway: true,
    probability: contract.probability,
  });
  if (!dist?.buckets?.length) return null;
  const idx = resolvedBucketIndex(dist.buckets, dist.kind, status, f?.resolution);
  if (idx == null || !dist.buckets[idx]) return null;
  return { label: bucketLabel(dist.buckets[idx]), isBinary: dist.kind === "binary" };
}

// When the forecast on the card was produced. `lastReforecastedAt` exists only in the
// session that ran or picked up a reforecast — rows don't carry it — so after a reload a
// saved reforecast is dated by its history snapshot, never by `createdAt` alone.
function lastForecastedAt(f) {
  const saved = liveContractMomentOf(f);
  const session = f.lastReforecastedAt;
  if (!session) return saved;
  if (!saved) return session;
  return Date.parse(session) > Date.parse(saved) ? session : saved;
}

// Shouted constants from shared sources (resolvedOutcome, forecastRowBadge), translated for
// display. An explicit map, so a data-derived bucket label passes through untouched.
const SENTENCE_CASE_LABELS = Object.freeze({
  OCCURRED: "Occurred",
  "DID NOT OCCUR": "Did not occur",
  TIED: "Tied",
  UNRESOLVED: "No answer yet",
  VOIDED: "Voided",
  RESOLVED: "Resolved",
  RUNNING: "Running",
  PREPARING: "Preparing",
  ERRORED: "Errored",
  CANCELLED: "Cancelled",
  // The synthesised catch-all bucket, which arrives lowercase from the
  // forecaster. In the map rather than title-cased generically for the same
  // reason as everything else here: a real bucket name ("Pittsburgh Steelers")
  // must pass through untouched, and only strings this file KNOWS get rewritten.
  other: "Other",
  Other: "Other",
});

const displayLabel = (label) =>
  typeof label === "string" ? (SENTENCE_CASE_LABELS[label] ?? label) : label;

/** "Weekly" for a forecast on a schedule; "" when it only updates by hand. */
function cadenceName(prefs) {
  const c = TRACKING_CAD[prefs?.cadIdx ?? 0];
  if (!c || c === "Never") return "";
  return sentenceCase(c);
}

// The lifecycle labels this card shows in the answer slot while a forecast has no answer yet.
const STATUS_TEXT = Object.freeze({
  RUNNING: "Forecasting",
  PREPARING: "Checking your question",
  ERRORED: "Didn't finish",
  CANCELLED: "Cancelled",
});

/** The answer slot: the live answer, a settled outcome, or what is happening instead. */
function CardAnswer({ f, outcome, won, showStatus, statusLabel }) {
  if (showStatus) {
    const running = statusLabel === "RUNNING" || statusLabel === "PREPARING";
    return (
      <div className="sy-fcard__answer">
        <span className="sy-fcard__state" style={{ color: running ? C.accent : C.gray }}>
          {running ? <Spinner size="sm" label={STATUS_TEXT[statusLabel]} /> : <IconAlert />}
          {STATUS_TEXT[statusLabel] || displayLabel(statusLabel)}
        </span>
      </div>
    );
  }
  if (outcome) {
    if (!outcome.namesOutcome) {
      return (
        <div className="sy-fcard__answer">
          <span className="sy-fcard__state" style={{ color: C.gray }}>
            {displayLabel(outcome.label)}
          </span>
        </div>
      );
    }
    // A yes/no question's buckets are themselves the verdict; a multi-outcome one names the
    // outcome that happened.
    const text =
      won && !won.isBinary
        ? displayLabel(won.label)
        : outcome.label === "OCCURRED"
          ? "Yes"
          : outcome.label === "DID NOT OCCUR"
            ? "No"
            : displayLabel(outcome.label);
    return (
      <div className="sy-fcard__answer">
        <span className="sy-fcard__label" style={{ color: C.gray }}>
          Resolved
        </span>
        <span className="sy-fcard__state" style={{ color: RESOLVED_GOLD }}>
          <IconCheck />
          <span className="sy-fcard__label" style={{ color: "inherit" }}>
            {text}
          </span>
        </span>
      </div>
    );
  }
  const a = answerSummary(f);
  if (!a || a.kind === "none" || !a.leader) {
    return (
      <div className="sy-fcard__answer">
        <span className="sy-fcard__pct sy-num" style={{ color: C.gray }}>
          –
        </span>
      </div>
    );
  }
  // The side the forecast leads with: the larger of Yes and No, or the leading NAMED outcome
  // (the catch-all only when it outweighs them all).
  return (
    <div
      className="sy-fcard__answer"
      title={
        a.kind === "binary"
          ? `${a.headlineText} ${a.headlineLabel}`
          : a.leaderStatement || undefined
      }
    >
      <span className="sy-fcard__label">{displayLabel(a.leader.label)}</span>
      <span className="sy-fcard__pct sy-num">{a.headlineText}</span>
    </div>
  );
}

/**
 * The card's "More" menu. Opens below its button (above it when the list would clip it),
 * closes on Escape, a click outside, or picking an item; arrow keys move between items.
 */
function CardMenu({ f, items }) {
  const [open, setOpen] = React.useState(false);
  const [up, setUp] = React.useState(false);
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector("button")?.focus();
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // telemetry-gesture: an outside click closes the card menu (dismissing)
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const toggle = (e) => {
    e.stopPropagation();
    if (!open) {
      // Open upward when the scrolling list has no room below the button.
      const b = btnRef.current?.getBoundingClientRect();
      let el = btnRef.current?.parentElement;
      while (el && el !== document.body && !/(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
        el = el.parentElement;
      }
      const limit = el && el !== document.body ? el.getBoundingClientRect().bottom : innerHeight;
      setUp(!!b && limit - b.bottom < 40 * items.length + 24);
    }
    setOpen((v) => !v);
  };
  const onKey = (e) => {
    const btns = [...(menuRef.current?.querySelectorAll("button") || [])];
    const i = btns.indexOf(document.activeElement);
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = btns.length;
      btns[(i + (e.key === "ArrowDown" ? 1 : -1) + n) % n]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  return (
    <div className="sy-fcard__menu" onClick={(e) => e.stopPropagation()}>
      <IconButton
        label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        ref={btnRef}
        onClick={toggle}
        {...ui("forecast-manager.card-menu", f.id)}
      >
        <IconMore />
      </IconButton>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Forecast actions"
          className="sy-menu"
          style={up ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {items.map((it, i) =>
            it === "divider" ? (
              <hr key={`d${i}`} />
            ) : (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                data-danger={it.danger ? "" : undefined}
                {...ui(it.ui["data-ui"], it.ui["data-ui-key"])}
                onKeyDown={onKey}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  it.onSelect(e);
                }}
              >
                {it.icon}
                {it.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The menu's items for one card. Absent handlers mean absent items, never dead ones. Each
 * names its control (`ui`) with a literal id, which the telemetry registry checks.
 */
function cardMenuItems(
  f,
  { watched, onSelect, onToggleWatch, onOpenShare, onOpenSettings, onDelete, batch },
) {
  if (batch) return batchMenuItems(batch);
  const shared = isReadOnlyRow(f);
  const items = [
    {
      key: "open",
      label: "Open",
      icon: <IconArrowRight />,
      ui: ui("forecast-manager.menu-open", f.id),
      onSelect: (e) => onSelect(f, e),
    },
    {
      key: "watch",
      ui: ui("forecast-manager.watch-toggle", f.id),
      label: watched ? "Remove from watchlist" : "Add to watchlist",
      icon: <IconEye />,
      onSelect: () => {
        onToggleWatch(f);
        toast(watched ? "Removed from your watchlist" : "Added to your watchlist", {
          kind: "success",
          ms: 2500,
        });
      },
    },
  ];
  if (typeof onOpenShare === "function")
    items.push({
      key: "share",
      label: "Share",
      icon: <IconShare />,
      ui: ui("forecast-manager.menu-share", f.id),
      onSelect: (e) => onOpenShare(f, e),
    });
  if (typeof onOpenSettings === "function")
    items.push({
      key: "settings",
      ui: ui("forecast-manager.tracking-settings", f.id),
      // A watched shared card opens the full tracking panel; an unwatched one only its access.
      label: shared && !watched ? "Access settings" : "Tracking settings",
      icon: <IconBell />,
      onSelect: () => onOpenSettings(f),
    });
  if (!shared && typeof onDelete === "function")
    items.push("divider", {
      key: "delete",
      label: "Delete forecast",
      ui: ui("forecast-manager.menu-delete", f.id),
      icon: <IconTrash />,
      danger: true,
      onSelect: () => onDelete(f),
    });
  return items;
}

/** The menu's items when several cards are picked: each acts on all of them. */
function batchMenuItems(batch) {
  const { rows, isWatched } = batch;
  const toWatch = rows.filter((r) => !isWatched(r)).length;
  const toUnwatch = rows.length - toWatch;
  const deletable = rows.filter((r) => !isReadOnlyRow(r)).length;
  const items = [];
  if (toWatch)
    items.push({
      key: "batch-watch",
      ui: ui("forecast-manager.batch-watch"),
      label: `Add ${toWatch} to watchlist`,
      icon: <IconEye />,
      onSelect: () => batch.setWatch(true),
    });
  if (toUnwatch)
    items.push({
      key: "batch-unwatch",
      ui: ui("forecast-manager.batch-unwatch"),
      label: `Remove ${toUnwatch} from watchlist`,
      icon: <IconEye />,
      onSelect: () => batch.setWatch(false),
    });
  items.push({
    key: "batch-clear",
    label: "Clear selection",
    icon: <IconClose />,
    ui: ui("forecast-manager.batch-clear"),
    onSelect: batch.clear,
  });
  if (deletable && batch.remove)
    items.push("divider", {
      key: "batch-delete",
      ui: ui("forecast-manager.batch-delete"),
      label: deletable === 1 ? "Delete 1 forecast" : `Delete ${deletable} forecasts`,
      icon: <IconTrash />,
      danger: true,
      onSelect: batch.remove,
    });
  return items;
}

/** One Forecasts card. `watched` says which section it sits in. */
export default function ForecastCard({
  f,
  watched,
  isActive,
  compareLane,
  compareMode,
  prefs,
  onSelect,
  onToggleWatch,
  onReorder,
  sortSection,
  onStack,
  onUnstack,
  onCardClick,
  dragProps,
  onOpenSettings,
  onOpenShare,
  onDelete,
  resolutionOverride = null,
  nowMs,
  picked = false,
  onPick,
  batch = null,
}) {
  const { laneColor, showActive } = laneRowStyle(compareLane, compareMode, isActive);
  const resolvedStatus = resolutionOverride || f?.resolution?.status;
  // A lean row draws its answer, dates and verdict from the list summary until its detail lands.
  const src = listSource(f);
  const won = resolvedWinner(src, resolvedStatus);
  const { showBadge, label: badgeLabel } = forecastRowBadge(f);
  const outcome = resolvedOutcome(src, nowMs, resolutionOverride);
  const statusInPill = !!showBadge && !outcome && f.contractStatus !== "resolved";
  const updated = lastForecastedAt(src);
  const ago = updated ? fmtRelative(updated) : "";
  const cadence = cadenceName(prefs);
  return (
    <div
      className="forecast-log-card sy-fcard"
      // "one row of the Forecast Manager" — the stable handle e2e counts rows by.
      data-testid="forecast-log-row"
      data-selected={showActive || laneColor ? "" : undefined}
      data-picked={picked ? "" : undefined}
      {...surface("forecast-manager.row", f.id)}
      data-collab={hasCollabBadge(f) ? "" : undefined}
      onClick={(e) => {
        // Shift- or Cmd-click picks cards for a batch action; a plain click opens one and
        // drops the batch. Compare mode keeps shift-click for its second lane.
        if (onPick && !compareMode && (e.shiftKey || e.metaKey || e.ctrlKey)) {
          onPick(f);
          return;
        }
        onPick?.(null);
        if (onCardClick) onCardClick(f, e);
        else onSelect(f, e);
      }}
      onMouseDown={suppressShiftSelect}
      {...ui("forecast-manager.row", f.id)}
      {...(dragProps ||
        sortableRowProps(f.id, sortSection || (watched ? "watchlist" : "recent"), onReorder, {
          onStack,
          onUnstack,
        }))}
      style={
        laneColor ? { borderColor: laneColor, boxShadow: `0 0 0 1px ${laneColor}` } : undefined
      }
    >
      {picked && (
        <span className="sy-fcard__pick" aria-hidden="true">
          <IconCheck />
        </span>
      )}
      <div className="sy-fcard__title">{forecastDisplayName(f)}</div>
      <div className="sy-fcard__foot">
        <div className="sy-fcard__meta">
          {/* A forecast opened from "Forecasts you may be interested in": not one the user asked. */}
          {isSayerManaged(f) && (
            <Chip
              data-fcard-origin="sooth-labs"
              title="Picked for you by Sooth Labs"
              style={{ color: "var(--c-accent)", flexShrink: 0 }}
            >
              From Sooth Labs
            </Chip>
          )}
          {f.alert ? (
            <span className="sy-fcard__moved" title="Moved past your alert threshold">
              <IconBell /> Moved
            </span>
          ) : null}
          {ago && (
            <span className="sy-fcard__meta-text" title="Last updated">
              {ago}
            </span>
          )}
          {cadence && (
            <span className="sy-fcard__cadence" title={`Reforecasts ${cadence.toLowerCase()}`}>
              <IconReforecast width={14} height={14} /> {cadence}
            </span>
          )}
          <CollabBadge f={f} onOpenShare={onOpenShare} />
        </div>
        <CardAnswer
          f={src}
          outcome={outcome}
          won={won}
          showStatus={statusInPill}
          statusLabel={badgeLabel}
        />
      </div>
      <CardMenu
        f={f}
        items={cardMenuItems(f, {
          watched,
          onSelect,
          onToggleWatch,
          onOpenShare,
          onOpenSettings,
          onDelete,
          batch,
        })}
      />
    </div>
  );
}
